import { useEffect, useRef, useState } from "react";
import { Trans } from "react-i18next";
import { Check, Info, Plus, Wallet } from "lucide-react";

import { featureFlags } from "@common/feature-flags";
import { Footer, PageContent } from "@/components/page";
import { ModalBackdrop, ModalWrapper, useMuiModal } from "@/components/modal";
import { LoadingSpinner } from "@/components/spinner";
import { Currency } from "@/components/currency";
import { cn } from "@/lib/utils";
import { useFeatureFlags } from "@/lib/runtime-config";
import { useShop } from "@/hooks/use-shop";
import { useUser } from "@/hooks/use-user";
import { usePaymentStatus } from "@/hooks/use-payment-status";
import { useRemovePayment } from "@/hooks/use-remove-payment";
import type { PaymentType } from "@/features/payment/utils";
import { AudioSlot } from "@/features/audio/kiosk-audio";
import {
  AbortedModal,
  CashPaymentMessageModal,
  HardwareErrorModal,
  PrinterErrorModal,
  ServerErrorModal,
  TimedOutModal,
} from "@/features/payment-cash/modals";
import {
  ChangeReturnDisplay,
  useAbortCashPayment,
} from "@/features/payment-cash/cash-payment-helpers";
import {
  AcceptedCashDisplay,
  CashImageCredits,
} from "@/features/payment-cash/accepted-cash-display";
import { LegacyAcceptedCashDisplay } from "@/features/payment-cash/legacy-accepted-cash-display";

// The cash payment of a purchase, shared by the v5 cash payment screen and the
// payment flow: polls the cash-agent and derives what the screen shows.

const BALANCE_CREDIT_STEP_MS = 4500;

export type CashPayment = ReturnType<typeof useCashPayment>;

export function useCashPayment(
  payment: PaymentType,
  options?: { enabled?: boolean },
) {
  const { data: shop } = useShop();
  const status = usePaymentStatus(payment, options);
  const { data: paymentStatus, error } = status;
  const abort = useAbortCashPayment();
  const { showModal } = useMuiModal();
  const printErrorShownRef = useRef(false);
  const [balanceCreditStepElapsed, setBalanceCreditStepElapsed] =
    useState(false);

  useEffect(() => {
    if (paymentStatus?.printFailed && !printErrorShownRef.current) {
      printErrorShownRef.current = true;
      showModal(<PrinterErrorModal transactionId={paymentStatus.sessionId} />);
    }
  }, [paymentStatus?.printFailed, paymentStatus?.sessionId, showModal]);

  const isWaitingToFinish =
    [
      "TOP_UP_IN_PROGRESS",
      "PAYMENT_IN_PROGRESS",
      "RECENTLY_FINISHED",
      "SUCCESS_BUT_PAYOUT_FAILED",
    ].includes(paymentStatus?.paymentStatus ?? "") &&
    !!paymentStatus &&
    !error &&
    paymentStatus.insertedAmount >= payment.price;
  const isWaitingForBackendToAnswer =
    abort.isPending ||
    (!!abort.data && paymentStatus?.paymentStatus === "PAYMENT_IN_PROGRESS");
  const inserted = paymentStatus?.insertedAmount ?? 0;
  const balanceCreditAmount = Math.max(
    paymentStatus?.creditedAmount ?? 0,
    paymentStatus?.result?.balanceCreditAmount ?? 0,
  );
  const shouldRunBalanceCreditStep =
    !!paymentStatus &&
    paymentStatus.kind === "purchase" &&
    paymentStatus.paymentStatus === "RECENTLY_FINISHED" &&
    balanceCreditAmount > 0 &&
    !error;
  const balanceCreditApplied =
    !!paymentStatus?.sideEffects.balanceApplied ||
    !!paymentStatus?.sideEffects.completed;
  const showBalanceCreditStep =
    shouldRunBalanceCreditStep &&
    (!balanceCreditStepElapsed || !balanceCreditApplied);

  // the balance credit step stays up a moment, even when the credit is instant
  useEffect(() => {
    if (!shouldRunBalanceCreditStep || balanceCreditStepElapsed) {
      return;
    }
    const timeout = window.setTimeout(() => {
      setBalanceCreditStepElapsed(true);
    }, BALANCE_CREDIT_STEP_MS);
    return () => window.clearTimeout(timeout);
  }, [balanceCreditStepElapsed, shouldRunBalanceCreditStep]);

  return {
    status,
    paymentStatus,
    error,
    abort,
    inserted,
    totalChange: Math.max(inserted - payment.price, 0),
    balanceCreditAmount,
    showBalanceCreditStep,
    showPayoutStatus:
      !!paymentStatus?.payoutStatus &&
      ["PayingOutCoin", "PayingOutNote", "WaitingForNoteRemoval"].includes(
        paymentStatus.payoutStatus,
      ),
    isFinishing:
      isWaitingToFinish ||
      !!paymentStatus?.hasEnded ||
      isWaitingForBackendToAnswer,
    canAbort: !isWaitingToFinish && !isWaitingForBackendToAnswer,
    // the money is in and the change is sorted out
    isPaid:
      paymentStatus?.paymentStatus === "RECENTLY_FINISHED" &&
      !showBalanceCreditStep,
    currencyCode: shop.currencyCode,
  };
}

/** The modals for every way a cash payment can end other than plain success. */
export function CashPaymentOutcomeModals({
  cash,
  payment,
  onPaid,
  onBackToSelector,
}: {
  cash: CashPayment;
  payment: PaymentType;
  // paid, but the change couldn't be paid out
  onPaid: () => void;
  // the payment is removed; pick a payment method again
  onBackToSelector: () => void;
}) {
  const { supportPhone } = useFeatureFlags();
  const removePayment = useRemovePayment();
  const status = cash.paymentStatus?.paymentStatus;

  return (
    <>
      {status === "SUCCESS_BUT_PAYOUT_FAILED" && (
        <ModalBackdrop>
          <ModalWrapper>
            <AudioSlot slot="modal.cash-payment.success-payout-failed" />
            <CashPaymentMessageModal
              title={<Trans>Visszajáró hiba</Trans>}
              onClose={onPaid}
            >
              <Trans values={{ supportPhone }}>
                A fizetés sikeres volt, de a visszajáró kiadása nem sikerült.
                Kérjük, hívd az ügyfélszolgálatot a {{ supportPhone }}{" "}
                telefonszámon!
              </Trans>
            </CashPaymentMessageModal>
          </ModalWrapper>
        </ModalBackdrop>
      )}
      {status === "ABORTED_BUT_PAYOUT_FAILED" && (
        <ModalBackdrop>
          <ModalWrapper>
            <AudioSlot slot="modal.cash-payment.aborted-payout-failed" />
            <CashPaymentMessageModal
              title={<Trans>Visszajáró hiba</Trans>}
              onClose={async () => {
                try {
                  await removePayment.mutateAsync({
                    key: payment.kioskPaymentNonceKey,
                    cause: "cancelled-by-user",
                  });
                } catch (e) {
                  console.error(e);
                }
                onBackToSelector();
              }}
            >
              <Trans values={{ supportPhone }}>
                A fizetés megszakadt, de a visszajáró kiadása nem sikerült.
                Kérjük, hívd az ügyfélszolgálatot a {{ supportPhone }}{" "}
                telefonszámon!
              </Trans>
            </CashPaymentMessageModal>
          </ModalWrapper>
        </ModalBackdrop>
      )}
      {status === "TIMED_OUT" && (
        <ModalBackdrop>
          <ModalWrapper>
            <TimedOutModal payment={payment} onDone={onBackToSelector} />
          </ModalWrapper>
        </ModalBackdrop>
      )}
      {status === "ABORTED" && (
        <ModalBackdrop>
          <ModalWrapper>
            <AbortedModal payment={payment} onDone={onBackToSelector} />
          </ModalWrapper>
        </ModalBackdrop>
      )}
      {status === "HARDWARE_ERROR" && (
        <ModalBackdrop>
          <ModalWrapper>
            <HardwareErrorModal payment={payment} onDone={onBackToSelector} />
          </ModalWrapper>
        </ModalBackdrop>
      )}
      {cash.error && (
        <ModalBackdrop>
          <ModalWrapper>
            <ServerErrorModal payment={payment} onDone={onBackToSelector} />
          </ModalWrapper>
        </ModalBackdrop>
      )}
    </>
  );
}

/**
 * Amounts, accepted cash and change. `heading` "status-only" leaves out the
 * plain "Kérjük fizess!" when the page header already asks for the money.
 */
export function CashPaymentBody({
  cash,
  payment,
  heading = "always",
}: {
  cash: CashPayment;
  payment: PaymentType;
  heading?: "always" | "status-only";
}) {
  const { data: shop } = useShop();
  const user = useUser();
  const { paymentStatus, showBalanceCreditStep } = cash;
  const returnsChangeToBalance = !!user.data && !shop.returnsChangeToLoggedInUser;

  return (
    <>
      <AudioSlot
        slot={
          showBalanceCreditStep
            ? "page.cash-payment.balance-credit"
            : cash.isFinishing
              ? "page.cash-payment.finishing"
              : "page.cash-payment"
        }
      />
      {showBalanceCreditStep && (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 overflow-hidden">
          <div className="absolute -left-16 -top-28 h-44 w-[48vw] rounded-b-[100%] bg-[#bdeff2]" />
          <div className="absolute -right-16 -top-28 h-44 w-[48vw] rounded-b-[100%] bg-[#bdeff2]" />
        </div>
      )}
      <PageContent
        className={cn(showBalanceCreditStep && "relative z-10 max-w-[1120px]")}
      >
        {cash.status.isPending && <LoadingSpinner />}
        {(heading === "always" ||
          showBalanceCreditStep ||
          cash.isFinishing) && (
          <h2
            className={cn(
              "mb-16 text-center font-helvetica text-6xl font-bold short:mb-8",
              showBalanceCreditStep &&
                "mb-14 text-[58px] leading-none text-[#0d6688]",
            )}
          >
            {showBalanceCreditStep ? (
              <Trans>Visszajáró jóváírása...</Trans>
            ) : cash.isFinishing ? (
              <Trans>Fizetés befejezése...</Trans>
            ) : (
              <Trans>Kérjük fizess!</Trans>
            )}
          </h2>
        )}
        <div
          className={cn(
            "mb-12 grid grid-cols-3 justify-center gap-x-5 short:mb-6",
            showBalanceCreditStep && "mb-12 text-[#0d6688]",
          )}
        >
          <Amount
            label={<Trans>Fizetendő</Trans>}
            amount={payment.price}
            emphasized={false}
            balanceCredit={showBalanceCreditStep}
          />
          <Amount
            label={<Trans>Fizetve</Trans>}
            amount={cash.inserted}
            emphasized
            balanceCredit={showBalanceCreditStep}
          />
          <Amount
            label={
              returnsChangeToBalance ? (
                <Trans>Visszajáró az egyenlegre</Trans>
              ) : (
                <Trans>Visszajáró</Trans>
              )
            }
            amount={cash.totalChange}
            emphasized={false}
            balanceCredit={showBalanceCreditStep}
          />
        </div>
      </PageContent>

      {showBalanceCreditStep ? (
        <BalanceCreditDisplay amount={cash.balanceCreditAmount} />
      ) : cash.showPayoutStatus ? (
        <ChangeReturnDisplay
          state={paymentStatus?.payoutStatus ?? "PrePayout"}
          totalChange={cash.totalChange || (paymentStatus?.insertedAmount ?? 0)}
          dispensed={paymentStatus?.dispensedAmount ?? 0}
        />
      ) : (
        <>
          {featureFlags.kiosk.newCashUi ? (
            <AcceptedCashDisplay
              currencyCode={shop.currencyCode}
              acceptedCoinValues={
                paymentStatus?.acceptedCoinValues ??
                (shop.currencyCode === "EUR" ? [0.5, 1, 2] : [100, 200])
              }
              acceptedNoteValues={paymentStatus?.acceptedNoteValues ?? null}
            />
          ) : (
            <LegacyAcceptedCashDisplay
              currencyCode={shop.currencyCode}
              acceptedCoinValues={paymentStatus?.acceptedCoinValues}
              acceptedNoteValues={paymentStatus?.acceptedNoteValues}
              emptyPlaceholder="-"
              coinFallback={
                shop.currency === "EUR" ? (
                  <Trans>
                    Csak 0.50, 1.00 és 2.00 € érméket fogad el a fizető
                    automata.
                  </Trans>
                ) : (
                  <Trans>
                    Csak 100 és 200 forintos érméket fogad el a fizető
                    automata.
                  </Trans>
                )
              }
            />
          )}
          <PageContent className="pt-4 short:pt-2">
            {returnsChangeToBalance && (
              <p className="mb-4 mt-4 flex items-center gap-x-3 text-2xl text-[#f45e22] short:mb-2 short:mt-2">
                <img src="/exclamation.svg" className="size-6" />
                <Trans>A visszajáró összeget feltöltjük az egyenlegedre.</Trans>
              </p>
            )}
            {shop.canGenerateInvoices && user.data && (
              <p className="flex items-center gap-x-1 text-2xl">
                <img src="/arrow-attention.svg" className="size-8" />
                <Trans>
                  Számládat egyszerűen letöltheted a my.bubbles.hu oldalról.
                </Trans>
              </p>
            )}
          </PageContent>
        </>
      )}
    </>
  );
}

function Amount({
  label,
  amount,
  emphasized,
  balanceCredit,
}: {
  label: React.ReactNode;
  amount: number;
  emphasized: boolean;
  balanceCredit: boolean;
}) {
  return (
    <div
      className={cn(
        "text-center",
        emphasized ? "text-5xl" : "text-3xl",
        balanceCredit &&
          (emphasized ? "text-[58px] leading-none" : "text-[34px] leading-none"),
      )}
    >
      <p className="mb-6">{label}</p>
      <p
        className={cn(
          "font-helvetica font-bold",
          balanceCredit && (emphasized ? "text-[50px]" : "text-[30px]"),
        )}
      >
        <Currency amount={amount} />
      </p>
    </div>
  );
}

export function CashPaymentFooter({ cash }: { cash: CashPayment }) {
  if (cash.showBalanceCreditStep) {
    return null;
  }
  return (
    <Footer
      attribution={
        featureFlags.kiosk.newCashUi &&
        !cash.showPayoutStatus && (
          <CashImageCredits
            currencyCode={cash.currencyCode}
            showNotes={!!cash.paymentStatus?.acceptedNoteValues}
          />
        )
      }
      helpText={
        <Trans>
          Itt látod a fizetendő összeget illetve az elfogadott érmék és
          bankjegyek listáját. A képernyő felső felén megjelenik a fizetendő és
          a már befizetett összeg. Ugyanúgy a visszaadásnál is jelzi a
          visszaadandó és a már visszaadott összeget. Az automata maximum 1000
          forintot tud visszaadni érmékben.
        </Trans>
      }
    />
  );
}

function BalanceCreditDisplay({ amount }: { amount: number }) {
  return (
    <PageContent className="relative z-10 max-w-[980px] pt-0">
      <div className="mx-auto rounded-[38px] border border-[#d7edf1] bg-[#f9feff]/95 px-16 py-9 text-[#0d6688] shadow-[0_18px_36px_rgba(14,102,136,0.16)]">
        <div className="relative mx-auto mb-5 size-32">
          <div className="absolute inset-0 animate-ping rounded-full bg-[#cceff2] opacity-35" />
          <div className="absolute inset-0 animate-pulse rounded-full bg-[#cceff2]" />
          <Wallet className="absolute left-1/2 top-1/2 size-16 -translate-x-1/2 -translate-y-1/2 animate-bounce text-[#0aa3b0] [animation-duration:1.8s]" />
          <div className="absolute bottom-3 right-2 flex size-12 items-center justify-center rounded-full border-4 border-white bg-[#12aeb8] text-white shadow-md">
            <Check className="size-7 stroke-[4]" />
          </div>
        </div>
        <p className="mb-6 text-center font-helvetica text-[34px] font-bold leading-tight">
          <Trans>A visszajárót jóváírjuk az egyenlegedre.</Trans>
        </p>
        <div className="mx-auto flex max-w-[820px] items-center justify-between rounded-3xl border-2 border-[#d7f3f6] bg-white px-8 py-5 shadow-[0_6px_18px_rgba(13,102,136,0.08)]">
          <span className="flex items-center gap-6 text-[30px] leading-none">
            <span className="flex size-12 items-center justify-center rounded-full bg-[#12aeb8] text-white">
              <Plus className="size-8 stroke-[4]" />
            </span>
            <span>
              <Trans>Jóváírandó visszajáró</Trans>
            </span>
          </span>
          <span className="font-helvetica text-[38px] font-bold leading-none text-[#12aeb8]">
            <Currency amount={amount} />
          </span>
        </div>
        <div className="mx-auto mt-4 flex justify-center gap-3">
          <span className="size-3 animate-bounce rounded-full bg-[#12aeb8] [animation-delay:0ms]" />
          <span className="size-3 animate-bounce rounded-full bg-[#12aeb8] [animation-delay:150ms]" />
          <span className="size-3 animate-bounce rounded-full bg-[#12aeb8] [animation-delay:300ms]" />
        </div>
        <div className="mx-auto my-7 max-w-[860px] border-t-2 border-dashed border-[#6bd4da]" />
        <div className="mx-auto flex max-w-[820px] items-center gap-5 text-[26px] leading-tight">
          <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-[#12aeb8] text-white">
            <Info className="size-8" />
          </span>
          <p>
            <Trans>
              Kérjük, várj pár másodpercet, amíg rögzítjük az egyenlegeden.
            </Trans>
          </p>
        </div>
      </div>
    </PageContent>
  );
}
